import React, { MutableRefObject, ReactNode, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import {
  Item,
  ItemContainer,
  ItemPrimary,
  Content,
  ItemAccessory,
} from '@components/Modals/Actions';
import Modal from '@components/Modals';
import { DropdownMenuOption } from '@app/types';
import { useHotkeyHandler } from '@hooks/use-hotkey-handler';
import { Key } from '@components/KeyboardShortcut';
import styles from './SelectOption.module.scss';

type OptionRef = MutableRefObject<HTMLElement>;

const Option = ({
  icon,
  title,
  action,
  idx,
  setOptionRef,
  focusOption,
  disabled,
  className,
}: DropdownMenuOption & {
  idx: number;
  setOptionRef: (idx: number, ref: OptionRef) => void;
  focusOption: (idx: number) => void;
}) => {
  const optionRef = useRef(null);
  const Icon = icon;

  useEffect(() => setOptionRef(idx, optionRef), []);

  return (
    <Item
      focus
      onClick={action}
      tabIndex={0}
      onMouseEnter={() => focusOption(idx)}
      ref={optionRef}
      disabled={disabled}
      className={className}
    >
      <ItemContainer className="option-container">
        <ItemPrimary className={cn(styles.optionDetails, 'option-details')}>
          {Icon && <Icon />}
          {title}
        </ItemPrimary>
        {!disabled && (
          <ItemAccessory>
            <Key>{idx + 1}</Key>
          </ItemAccessory>
        )}
      </ItemContainer>
    </Item>
  );
};

export default function SelectOptionModal({
  closeModal,
  header,
  options,
  onModalOpen,
}: {
  closeModal: () => void;
  header: ReactNode;
  options: DropdownMenuOption[];
  onModalOpen?: () => void;
}) {
  const [selectedOptionIdx, setSelectedOptionIdx] = useState(null);
  const optionRefs = useRef<OptionRef[]>([]);
  const setOptionRef = (idx: number, ref: OptionRef) => (optionRefs.current[idx] = ref);

  const focusOption = (idx: number) => {
    const optionRef = optionRefs.current[idx];

    if (optionRef) {
      setSelectedOptionIdx(idx);
      optionRef.current.focus();
    } else {
      setSelectedOptionIdx(null);
    }
  };

  useHotkeyHandler(
    {
      handler: (e) => {
        const matchedOptionRef = optionRefs.current[parseInt(e.key) - 1];

        if (matchedOptionRef) {
          matchedOptionRef.current.click();
        } else if (e.key == 'ArrowUp' || (e.shiftKey && e.key == 'Tab')) {
          const currentIdx = selectedOptionIdx === null ? options.length : selectedOptionIdx;
          const prevIdx = currentIdx === 0 ? options.length - 1 : currentIdx - 1;
          focusOption(prevIdx);
          e.preventDefault();
          e.stopPropagation();
        } else if (e.key == 'ArrowDown' || e.key == 'Tab') {
          const currentIdx = selectedOptionIdx === null ? -1 : selectedOptionIdx;
          const nextIdx = currentIdx === options.length - 1 ? 0 : currentIdx + 1;
          focusOption(nextIdx);
          e.preventDefault();
          e.stopPropagation();
          // we handle Enter here for cases where this is rendered adjacent to a navigable list, e.g. Search or People views
        } else if (e.key == 'Enter') {
          const selectedOption = optionRefs.current[selectedOptionIdx];
          if (selectedOption) {
            selectedOption.current.click();
            e.preventDefault();
          }
        }
      },
      priority: 1,
    },
    [selectedOptionIdx],
  );

  useEffect(() => onModalOpen?.(), []);

  return (
    <Modal className="modal-small" isOpen={true} onRequestClose={closeModal}>
      <div className={cn('modal-header', styles.header)}>{header}</div>
      <Content>
        {options.map((option, idx) => (
          <Option
            key={idx}
            idx={idx}
            setOptionRef={setOptionRef}
            focusOption={focusOption}
            {...option}
          />
        ))}
      </Content>
    </Modal>
  );
}
